import { useEffect, useMemo, useRef, useState } from "react";
import { useChatContext } from "../../components/Chat/providers/ChatProvider";
import type { AssistantPart, AssistantSubagentPart, TimelineEntry } from "../types";

function subagentParts(parts: AssistantPart[]): AssistantSubagentPart[] {
  return parts.filter((p): p is AssistantSubagentPart => p.type === "subagent");
}

/** Pure core: union subagent parts from the live turn and persisted messages,
 *  keep only running, dedupe by job_id (live part wins over persisted).
 *  Exported for unit testing without React. */
export function collectRunningSubagents(
  liveParts: AssistantPart[],
  history: TimelineEntry[],
): AssistantSubagentPart[] {
  const byId = new Map<string, AssistantSubagentPart>();
  // Persisted first, then live — live overwrites, so live wins on collision.
  for (const entry of history) {
    const content = (entry as { content?: unknown }).content;
    if (Array.isArray(content)) {
      for (const p of subagentParts(content as AssistantPart[])) byId.set(p.job_id, p);
    }
  }
  for (const p of subagentParts(liveParts)) byId.set(p.job_id, p);
  return [...byId.values()].filter((p) => p.status === "running");
}

export interface RunningSubagentRow {
  part: AssistantSubagentPart;
  terminal: boolean;
}

const PULSE_WINDOW_MS = 8_000;

/** Union of all subagent parts (any status) from the live turn and persisted
 *  messages, deduped by job_id (live part wins over persisted). */
function allSubagents(liveParts: AssistantPart[], history: TimelineEntry[]): AssistantSubagentPart[] {
  const byId = new Map<string, AssistantSubagentPart>();
  for (const entry of history) {
    const content = (entry as { content?: unknown }).content;
    if (Array.isArray(content)) {
      for (const p of subagentParts(content as AssistantPart[])) byId.set(p.job_id, p);
    }
  }
  for (const p of subagentParts(liveParts)) byId.set(p.job_id, p);
  return [...byId.values()];
}

/** Pure pulse computation. Given currently-running parts, previous
 * render's running parts, persisted terminalSince map, current time,
 * window, all deduped parts (for looking up the finished part) —
 * returns rows plus the updated terminalSince map. */
export function computeRows(
  running: AssistantSubagentPart[],
  prevRunning: AssistantSubagentPart[],
  terminalSince: Map<string, number>,
  now: number,
  windowMs: number,
  allParts: AssistantSubagentPart[] = running,
): { rows: RunningSubagentRow[]; terminalSince: Map<string, number> } {
  const next = new Map(terminalSince);
  const runningIds = new Set(running.map((p) => p.job_id));

  // Any id that was running last render but isn't running now just finished.
  for (const prev of prevRunning) {
    if (!runningIds.has(prev.job_id) && !next.has(prev.job_id)) next.set(prev.job_id, now);
  }

  // Drop ids whose pulse window has elapsed.
  for (const [id, since] of [...next.entries()]) {
    if (now - since >= windowMs) next.delete(id);
  }

  const rows: RunningSubagentRow[] = running.map((part) => ({ part, terminal: false }));
  const byId = new Map(allParts.map((p) => [p.job_id, p]));
  for (const id of next.keys()) {
    const part = byId.get(id);
    if (part && !runningIds.has(id)) rows.push({ part, terminal: true });
  }

  return { rows, terminalSince: next };
}

/** Running subagents for the current chat, live-fed by the subagent_event
 *  stream via the parts the thread already maintains. Chat-scoped by
 *  construction: it only sees this session's parts. Recently-finished
 *  subagents stay in the returned list for a short pulse window so their
 *  completion is noticeable before they disappear. */
export function useRunningSubagents(): RunningSubagentRow[] {
  const { chat } = useChatContext();
  const liveParts = chat.turn.assistantParts;
  const history = chat.history;

  const running = useMemo(() => collectRunningSubagents(liveParts, history), [liveParts, history]);
  const all = useMemo(() => allSubagents(liveParts, history), [liveParts, history]);

  const prevRunningRef = useRef<AssistantSubagentPart[]>([]);
  const terminalSinceRef = useRef<Map<string, number>>(new Map());
  const [, tick] = useState(0);

  const { rows, terminalSince } = computeRows(
    running, prevRunningRef.current, terminalSinceRef.current, Date.now(), PULSE_WINDOW_MS, all,
  );
  // INTENTIONAL render-time ref write (not in a useEffect). computeRows'
  // "just finished" detection is guarded by `!next.has(id)`, and on
  // StrictMode's double-invoke both passes compute against the same
  // prevRunning/terminalSince snapshot and write back the same values, so
  // this is idempotent and safe to run during render. Moving these into a
  // useEffect would defer the update by a render pass and break the
  // completion pulse (the finished id would never be captured in time).
  prevRunningRef.current = running;
  terminalSinceRef.current = terminalSince;

  // While any terminal pulse is active, re-render once after the window so
  // the row removes itself even if no new parts arrive.
  useEffect(() => {
    if (terminalSince.size === 0) return;
    const t = setTimeout(() => tick((n) => n + 1), PULSE_WINDOW_MS + 250);
    return () => clearTimeout(t);
  }, [rows.length, terminalSince.size]);

  return rows;
}
